import { useState, useEffect } from "react";
import type { BackendOrder, TabType } from "../components/Tracking/types";
import { getOrdersByStageV2 } from "./useSupabase";

export interface UseTrackingProps {
  searchTerm: string;
}

export interface UseTrackingReturn {
  orders: BackendOrder[];
  loading: boolean;
  refetch: () => void;
}

export const useTracking = ({ 
  searchTerm
}: UseTrackingProps): UseTrackingReturn => {
  const [allOrders, setAllOrders] = useState<BackendOrder[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch orders from the database
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await getOrdersByStageV2({
        limit: 1000, // Get all orders for frontend filtering and pagination
        offset: 0,
        ...(searchTerm && { search: searchTerm }),
      });
      
      // Handle response structure: array of orders
      if (Array.isArray(response)) {
        // Use the data directly since it now comes with complete product info
        setAllOrders(response as unknown as BackendOrder[]);
      } else {
        setAllOrders([]);
      }
    } catch (error) {
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [searchTerm]);

  return {
    orders: allOrders, // Return all orders so frontend filtering works
    loading,
    refetch: fetchOrders,
  };
};

// Utility functions for filtering and counting orders
export const filterOrdersByTab = (
  orders: BackendOrder[],
  activeTab: TabType,
  searchTerm: string
): BackendOrder[] => {
  return orders.filter((order) => {
    const orderNumber = order.order_number || order.id?.toString() || "";
    const customerName = order.customer_name || "Unknown Customer";
    const matchesSearch =
      orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase());

    // For backend orders, map stage names properly
    const stageName = order.stage_name?.toLowerCase() || "";
    let matchesTab = false;
    if (activeTab === "pending") {
      // Map Processing and Confirmed to pending tab
      matchesTab = stageName === "pending" || stageName === "confirmed" || stageName === "processing";
    } else if (activeTab === "shipped") {
      matchesTab = stageName === "shipped";
    } else if (activeTab === "delivered") {
      matchesTab = stageName === "delivered";
    }
    
    return matchesTab && matchesSearch;
  });
};

export const getOrderCountByTab = (orders: BackendOrder[], tab: TabType): number => {
  return orders.filter((order) => {
    const stageName = order.stage_name?.toLowerCase() || "";
    if (tab === "pending") {
      // Map Processing and Confirmed to pending tab
      return stageName === "pending" || stageName === "confirmed" || stageName === "processing";
    } else if (tab === "shipped") {
      return stageName === "shipped";
    } else if (tab === "delivered") {
      return stageName === "delivered";
    }
    return false;
  }).length;
};
